import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ShareMenu } from '../components/ShareMenu';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NewsletterJotform from '../components/NewsletterJotform';
import usePageTitle from '../hooks/usePageTitle';
import { enhanceAccessibleHtml } from '../utils/a11yHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same as team images: prepend BACKEND_URL to /uploads/... so img src matches how team photo_url is loaded (e.g. /api/uploads/... or full origin)
function rewriteUploadUrlsInHtml(html) {
  if (!html || typeof html !== 'string') return html;
  const base = (BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (!base) return html;
  return html.replace(/\ssrc=(["'])\/uploads\//g, ` src=$1${base}/uploads/`);
}

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/** Build a full HTML document for iframe embed. If content is already a full document (e.g. Mailchimp), use as-is to preserve layout.
 *  Rewrites /uploads/... to BACKEND_URL/uploads/... so images use the same path as team photos (e.g. /api/uploads/...). */
function newsletterDoc(html) {
  const rewritten = rewriteUploadUrlsInHtml(html || '');
  const raw = rewritten.trim();
  const isFullDocument = /^\s*<!DOCTYPE\s+html\s*>/i.test(raw) || /^\s*<html[\s>]/i.test(raw);
  if (isFullDocument) {
    return raw;
  }
  const safe = enhanceAccessibleHtml(
    (rewritten || '')
      .replace(/<\/body\>/gi, '&lt;/body&gt;')
      .replace(/<\/html\>/gi, '&lt;/html&gt;'),
  );
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:0;padding:0;}img{max-width:100%;height:auto;}table{max-width:100%;}</style></head><body>${safe}</body></html>`;
}

export default function ResourceDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const isNewsletter = location.pathname.includes('newsletters');
  const isRepresentation = location.pathname.includes('representations');
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageTitle(item?.title || (loading ? 'Resources' : 'Resource'));

  useEffect(() => {
    if (!id) return;
    const url = isNewsletter
      ? `${API}/admin/newsletters/public/${id}`
      : isRepresentation
        ? `${API}/admin/representations/public/${id}`
        : `${API}/admin/regulatory-updates/public/${id}`;
    axios
      .get(url)
      .then((res) => setItem(res.data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id, isNewsletter, isRepresentation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48 mb-6" />
              <div className="h-10 bg-gray-200 rounded w-3/4 mb-4" />
              <div className="h-4 bg-gray-100 rounded w-32 mb-8" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-5/6" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-500 mb-6">Resource not found.</p>
            <Link to="/resources" className="text-[#C8473A] hover:underline font-medium">
              Back to Resources
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const title = item.title;
  const publishedDate = item.published_date;
  const content = isNewsletter ? item.content : item.content || item.summary || '';
  const bodyHtml = !isNewsletter
    ? enhanceAccessibleHtml(rewriteUploadUrlsInHtml(content), { pageTitle: title })
    : content;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareDescription = (content || '').replace(/<[^>]*>/g, '').trim().slice(0, 200);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Resources
            </Link>
            <ShareMenu
              url={shareUrl}
              title={title}
              description={shareDescription}
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              }
            />
          </div>

          <article className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <Calendar className="w-4 h-4" />
              {formatDate(publishedDate)}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-6">{title}</h1>
            {isNewsletter ? (
              <iframe
                srcDoc={newsletterDoc(content)}
                title={title}
                className="w-full min-h-[600px] border-0 rounded-lg"
                sandbox="allow-same-origin"
              />
            ) : (
              <div
                className="prose prose-lg max-w-none prose-headings:text-[#211F1F] prose-a:text-[#9b352c] hover:prose-a:text-[#7a2a1f] hover:prose-a:underline prose-img:rounded-lg"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            )}
            {isNewsletter && item.jotform_form_id && (
              <div className="mt-8" data-testid="newsletter-jotform">
                <NewsletterJotform formId={item.jotform_form_id} title={`${title} form`} />
              </div>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
